import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { AuthService } from '@vanilla/services/AuthService.js';
import { AzureAdAuthService } from '@vanilla/services/AzureAdAuthService.js';
import { EMAIL_ERROR_MESSAGE, isValidEmail } from '@vanilla/utils/EmailValidation.js';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function LoginPage() {
    const { login, loginWithMicrosoft, initializing, ssoPending, error } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [ssoSubmitting, setSsoSubmitting] = useState(false);
    const [localError, setLocalError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const msConfigured = useMemo(() => AzureAdAuthService.isConfigured(), []);
    const busy = submitting || initializing || ssoSubmitting || ssoPending;

    useEffect(() => {
        const onPageShow = async (event) => {
            if (AuthService.isAuthenticated()) return;
            if (!AzureAdAuthService.isConfigured()) {
                setSsoSubmitting(false);
                return;
            }
            try {
                await AzureAdAuthService.handleRedirectPromise();
            } catch { /* ignore */ }
            if (event?.persisted) {
                AzureAdAuthService.clearStaleInteraction();
            }
            setSsoSubmitting(false);
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (busy) return;
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail || !password) {
            setLocalError('Please enter both email and password.');
            return;
        }
        if (!isValidEmail(normalizedEmail)) {
            setLocalError(EMAIL_ERROR_MESSAGE);
            return;
        }
        setSubmitting(true);
        setLocalError('');
        try {
            await login(normalizedEmail, password);
        } catch (err) {
            setLocalError(err?.message || 'Login failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleMicrosoft = async () => {
        if (busy) return;
        setLocalError('');
        setSsoSubmitting(true);
        try {
            await loginWithMicrosoft();
        } finally {
            // Redirect usually navigates away; if it didn't, re-enable buttons
            if (!AuthService.isAuthenticated()) setSsoSubmitting(false);
        }
    };

    return (
        <div id="loginScreen" className="login-screen">
            <Card unstyled className="login-card">
                <CardHeader className="login-brand space-y-0 p-0">
                    <img src="/src/assets/bezohminds_logo_transparent.png" alt="Bezohminds CRM" className="login-brand-logo" width="280" height="64" />
                </CardHeader>

                <CardContent className="p-0">
                <div id="loginStepSignIn" className="login-step">
                    <p className="login-tagline">Manage customers, track deals, forecast revenue, and grow your sales pipeline.</p>
                    <form className="login-form" onSubmit={handleSubmit}>
                        <Label unstyled htmlFor="loginEmail">Email</Label>
                        <Input
                            unstyled
                            type="email"
                            id="loginEmail"
                            className="form-input"
                            placeholder="Enter email"
                            autoComplete="username"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <Label unstyled htmlFor="loginPassword">Password</Label>
                        <div className="password-input-wrap">
                            <Input
                                unstyled
                                type={showPassword ? 'text' : 'password'}
                                id="loginPassword"
                                className="form-input"
                                placeholder="Enter password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                id="loginPasswordToggle"
                                aria-pressed={showPassword ? 'true' : 'false'}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                onClick={() => setShowPassword((v) => !v)}
                            >
                                {showPassword ? (
                                    <svg id="loginPasswordToggleHideIcon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M3 3L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                        <path d="M10.6 10.6C10.9 10.2 11.4 10 12 10C13.6569 10 15 11.3431 15 13C15 13.6 14.8 14.1 14.4 14.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M6.5 6.6C4.1 8.5 2.7 10.7 2 12C4.5 16.8 8.1 19 12 19C13.7 19 15.3 18.6 16.7 17.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M9.8 5.2C10.5 5.1 11.2 5 12 5C15.9 5 19.5 7.2 22 12C21.3 13.3 19.9 15.5 17.5 17.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                ) : (
                                    <svg id="loginPasswordToggleShowIcon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M2 12C4.5 7.2 8.1 5 12 5C15.9 5 19.5 7.2 22 12C19.5 16.8 15.9 19 12 19C8.1 19 4.5 16.8 2 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="1.8" />
                                    </svg>
                                )}
                            </button>
                        </div>
                        <Button type="submit" className="btn btn-primary" variant="default" id="loginSubmitBtn" disabled={busy}>
                            {busy && !ssoSubmitting ? 'Signing in...' : 'Sign in'}
                        </Button>
                    </form>
                </div>

                {msConfigured && (
                    <>
                        <div id="loginSsoDivider" className="login-divider">
                            <Separator className="flex-1 w-auto" />
                            <span className="login-divider-text">or</span>
                            <Separator className="flex-1 w-auto" />
                        </div>

                        <div id="loginStepSso" className="login-step">
                            <div className="login-form login-form-sso">
                                <Button type="button" className="btn btn-ms" variant="secondary" id="loginMicrosoftBtn" disabled={busy} onClick={handleMicrosoft}>
                                    <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true"><rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" /><rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" /></svg>
                                    Sign in with Microsoft
                                </Button>
                                <p className="login-tagline">Manage customers, track deals, forecast revenue, and grow your sales pipeline.</p>
                            </div>
                        </div>
                    </>
                )}
                <p id="loginError" className={`login-error${(error || localError) ? '' : ' hidden'}`} role="alert">{error || localError}</p>
                </CardContent>
            </Card>
        </div>
    );
}
